import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { map, Observable, startWith } from 'rxjs';
import { GeneralDataSharingService } from 'src/app/core/global-data-sharing/general-data-sharing.service';
import { BaseComponent } from 'src/app/core/helper/base.component';
import { RouteHistoryService } from 'src/app/core/helper/route-history.service';
import { RedirectUrlService } from 'src/app/core/helper/redirect-url.service';
import { OutletRightsDTO } from 'src/app/core/outlet-cache/model/outlet';
import { OutletCacheService } from 'src/app/core/outlet-cache/outlet-cache.service';
import { UserCacheService } from 'src/app/core/user/user-cache.service';

@Component({
  selector: 'app-outlet-selection',
  templateUrl: './outlet-selection.component.html',
  styleUrls: ['./outlet-selection.component.scss']
})
export class OutletSelectionComponent extends BaseComponent implements OnInit {
  outlets: OutletRightsDTO[] = [];
  previousOutlet: OutletRightsDTO;
  outletForm = new FormControl<OutletRightsDTO | string>('');
  @Output() outletSelected = new EventEmitter<boolean>(false);
  filteredOutletRights: Observable<OutletRightsDTO[]> = new Observable<
    OutletRightsDTO[]
  >();
  selectedOutletCountry: string = '';
  userAdminCountries: string[] = [];

  constructor(
    private outletServiceCache: OutletCacheService,
    private userCacheService: UserCacheService,
    private router: Router,
    private generalDataSharingService: GeneralDataSharingService,
    private routeHistoryService: RouteHistoryService,
    private redirectUrlService: RedirectUrlService,
    private toastrService: ToastrService
  ) {
    super();
  }

  ngOnInit(): void {
    this.getOutletsForUser();
    this.setSelectedOutlet();
  }

  setSelectedOutlet() {
    this.outletServiceCache
      .getSelectedOutlet()
      .subscribe((selectedOutlet: OutletRightsDTO) => {
        if (selectedOutlet) {
          this.outletSelected.emit(true);
          this.outletForm.setValue(selectedOutlet);
        }
      });
  }

  getOutletsForUser() {
    this.outletServiceCache
      .getOutlets()
      .pipe(this.unsubscribeOnDestroy)
      .subscribe((outlets: OutletRightsDTO[]) => {
        this.outlets = outlets;
        this.filterOutlets();
      });
  }

  displayFn(outletRights: OutletRightsDTO): string {
    let storedOutlet;
    let stringOutlet = sessionStorage.getItem('presto-outlet');
    storedOutlet = stringOutlet !== null ? JSON.parse(stringOutlet) : {};

    if (storedOutlet && !outletRights) {
      return storedOutlet && storedOutlet.outletName
        ? storedOutlet.outletName
        : '';
    } else {
      return outletRights && outletRights.outlet.identity
        ? outletRights.outlet.identity.name
        : '';
    }
  }

  setOutlet(outlet: OutletRightsDTO) {
    this.outletServiceCache.setSelectedOutlet(outlet);
    this.toastrService.info((
      $localize`:@@toastr-info-outlet-selected:Outlet has been selected.`
    ))
  }

  canVisit() {
    this.outletServiceCache.outletSelected$.next(true);
  }

  redirectToDashboard(outlet: OutletRightsDTO) {
    if (outlet === this.previousOutlet) {
      return;
    }
    this.previousOutlet = outlet;

    //a page the user asked for but could not be let into without an outlet, now they have one
    const requestedUrl = this.redirectUrlService.consume();

    if (requestedUrl) {
      this.router.navigateByUrl(requestedUrl);
      return;
    }

    const previousRoute = this.routeHistoryService.getPreviousUrl();

    const isLoggedIn = !!this.userCacheService.get();
    //without the query params, so that a search driven by the url is not thrown away here
    const currentRoute = this.router.url.split('?')[0];

    if (isLoggedIn && currentRoute !== '/dashboard/product-search') {
      // navigate only if previous route is invalid(user is not logged in)
      if (!previousRoute) {
        this.router.navigate(['/dashboard/product-search']);
        setTimeout(() => this.generalDataSharingService.deleteData(), 100);
      }
    }
  }

  private filterOutlets() {
    this.filteredOutletRights = this.outletForm.valueChanges.pipe(
      startWith(''),
      map((value) => {
        const text =
          typeof value === 'string' ? value : value?.outlet.identity.name;
        return text ? this._filter(text) : this.outlets.slice();
      })
    );
  }

  private _filter(text: string): OutletRightsDTO[] {
    const filterValue = text.toLowerCase();
    return this.outlets.filter((outletRights: OutletRightsDTO) =>
      outletRights.outlet.identity.name.toLowerCase().includes(filterValue)
    );
  }

}


